import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface BranchContextType {
  selectedBranchId: string | null;
  selectedBranchName: string;
  setSelectedBranch: (branchId: string | null, branchName: string) => void;
  isAllBranches: boolean;
  refreshTrigger: number;
  triggerBranchRefresh: () => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

interface BranchProviderProps {
  children: ReactNode;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedBranchName, setSelectedBranchName] = useState('All Branches');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Initialize branch selection based on user role
  useEffect(() => {
    if (user) {
      if (user.role === 'branch_manager' && user.branchId) {
        // Branch managers are locked to their assigned branch
        setSelectedBranchId(user.branchId);
        setSelectedBranchName(''); // Will be set by Layout component
      } else {
        // Admins and inventory managers default to "All Branches"
        setSelectedBranchId(null);
        setSelectedBranchName('All Branches');
      }
    }
  }, [user]);

  const setSelectedBranch = (branchId: string | null, branchName: string) => {
    console.log('BranchContext setSelectedBranch called:', { branchId, branchName, userRole: user?.role });
    
    // For branch managers, always allow setting the branch (they can't change it anyway)
    // For admins/inventory managers, they can change branches
    setSelectedBranchId(branchId);
    setSelectedBranchName(branchName);
    console.log('Branch context updated:', { selectedBranchId: branchId, selectedBranchName: branchName });
  };

  const triggerBranchRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const isAllBranches = selectedBranchId === null && selectedBranchName === 'All Branches';

  const value: BranchContextType = {
    selectedBranchId,
    selectedBranchName,
    setSelectedBranch,
    isAllBranches,
    refreshTrigger,
    triggerBranchRefresh
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export const useBranch = (): BranchContextType => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export default BranchContext;